import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, X } from "lucide-react";
import { BrokerMark, Eyebrow } from "@/components/shared/Primitives";
import { useCompare } from "@/context/CompareContext";
import { useBrokers } from "@/context/BrokersContext";
import { Switch } from "@/components/ui/switch";
import { Reveal } from "@/components/motion/Reveal";
import { money } from "@/lib/format";

const GROUPS = [
  { title: "Regulation & safety", rows: [
    ["Regulators", (b) => b.entities.map((e) => e.regulator).join(", ")],
    ["Primary entity", (b) => `${b.entities[0].name} (${b.entities[0].jurisdiction})`],
    ["Trust score", (b) => `${b.trust.toFixed(1)} / 10`],
    ["Founded", (b) => String(b.founded)],
  ]},
  { title: "Trading costs", rows: [
    ["Pricing model", (b) => b.spreadModel],
    ["EUR/USD spread", (b) => `${b.eurUsdSpread.toFixed(1)} pips`],
    ["Commission (per lot RT)", (b) => (b.commission ? `$${b.commission.toFixed(2)}` : "None")],
    ["All-in EUR/USD", (b) => `$${(b.eurUsdSpread * 10 + b.commission).toFixed(2)} / lot`],
    ["Execution (median)", (b) => `${b.executionMs} ms`],
  ]},
  { title: "Swap conditions", rows: [
    ["EUR/USD long / short", (b) => (b.swapSource === "Unavailable" ? "Unavailable" : `${money(b.swaps.EURUSD.long)} / ${money(b.swaps.EURUSD.short)}`)],
    ["XAU/USD long / short", (b) => (b.swapSource === "Unavailable" ? "Unavailable" : `${money(b.swaps.XAUUSD.long)} / ${money(b.swaps.XAUUSD.short)}`)],
    ["Swap-free", (b) => (b.swapFree ? b.swapFreeNote : "Not offered")],
    ["Swap data", (b) => b.swapSource],
  ]},
  { title: "Funding", rows: [
    ["Minimum deposit", (b) => `$${b.minDeposit}`],
    ["Methods", (b) => b.funding.join(", ")],
    ["Withdrawal time", (b) => b.withdrawalTime],
    ["Withdrawal fee", (b) => b.withdrawalFee],
  ]},
  { title: "Platforms & promotions", rows: [
    ["Platforms", (b) => b.platforms.join(", ")],
    ["Deposit bonus", (b) => b.depositBonus || "None"],
    ["Max leverage", (b) => b.entities.map((e) => e.leverage).join(" / ")],
  ]},
];

export default function Compare() {
  const { slugs, toggle, clear } = useCompare();
  const { brokers: allBrokers, getBroker } = useBrokers();
  const [diff, setDiff] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const brokers = slugs.map(getBroker).filter(Boolean);
  const cols = brokers.length < 3 ? brokers.length + 1 : 3;
  const template = `220px repeat(${cols}, minmax(0,1fr))`;

  return (
    <div className="container-x py-12 lg:py-16" data-testid="compare-page">
      <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Eyebrow className="mb-4">Side-by-side comparison</Eyebrow>
          <h1 className="t-h1">Compare up to three brokers.</h1>
          <p className="mt-4 max-w-xl t-lead">Identical categories, verified data, nothing hidden. Toggle differences to see where brokers actually diverge.</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><Switch checked={diff} onCheckedChange={setDiff} data-testid="compare-diff-toggle" />Show differences only</label>
          {brokers.length > 0 && <button onClick={clear} className="text-xs text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="compare-clear">Clear</button>}
        </div>
      </Reveal>

      <div className="card card-plain mt-12 overflow-hidden">
        <div className="sticky top-[72px] z-20 grid border-b border-[var(--line)] bg-white/90 backdrop-blur-xl" style={{ gridTemplateColumns: template }} data-testid="compare-header">
          <div className="p-5"><p className="eyebrow">{brokers.length}/3 selected</p></div>
          {brokers.map((b) => (
            <div key={b.slug} className="p-5 border-l border-[var(--line)] flex items-start justify-between gap-2" data-testid={`compare-col-${b.slug}`}>
              <div className="flex items-center gap-3 min-w-0"><BrokerMark broker={b} size={36} /><div className="min-w-0"><Link to={`/brokers/${b.slug}`} className="text-sm font-medium tracking-[-0.01em] hover:text-[var(--cobalt)] block truncate">{b.name}</Link><a href={b.referral} target="_blank" rel="noreferrer sponsored" className="link-arrow text-[12px]" data-testid={`compare-visit-${b.slug}`}>Visit <ArrowUpRight size={12} /></a></div></div>
              <button onClick={() => toggle(b.slug, b.short)} className="text-[var(--ink-4)] hover:text-[var(--ink)]" aria-label="Remove" data-testid={`compare-remove-${b.slug}`}><X size={15} /></button>
            </div>
          ))}
          {brokers.length < 3 && (
            <div className="p-5 border-l border-[var(--line)] relative">
              <button onClick={() => setPickerOpen(!pickerOpen)} className="pill h-9 text-[13px]" data-testid="compare-add-button"><Plus size={14} /> Add broker</button>
              {pickerOpen && (
                <div className="absolute left-5 top-16 z-30 w-64 bg-white border border-[var(--line)] shadow-xl p-2" data-testid="compare-picker">
                  {allBrokers.filter((b) => !slugs.includes(b.slug)).map((b) => <button key={b.slug} onClick={() => { toggle(b.slug, b.short); setPickerOpen(false); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-[var(--canvas)]" data-testid={`compare-pick-${b.slug}`}><BrokerMark broker={b} size={22} className="!rounded-md" />{b.name}</button>)}
                </div>
              )}
            </div>
          )}
        </div>

        {brokers.length === 0 ? (
          <div className="p-16 text-center" data-testid="compare-empty">
            <p className="t-h5">Nothing to compare yet</p>
            <p className="mt-2 text-sm text-[var(--ink-3)]">Add brokers from the directory, finder results or the picker above.</p>
            <div className="mt-6 flex justify-center gap-2"><Link to="/brokers/finder" className="btn-primary">Open finder</Link><Link to="/brokers" className="btn-outline">Browse directory</Link></div>
          </div>
        ) : GROUPS.map((g) => {
          const rows = g.rows.filter(([, fn]) => !diff || new Set(brokers.map(fn)).size > 1);
          if (!rows.length) return null;
          return (
            <div key={g.title} data-testid={`compare-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="px-5 py-3 bg-[var(--canvas)] border-b border-[var(--line)]"><p className="eyebrow">{g.title}</p></div>
              {rows.map(([label, fn]) => {
                const vals = brokers.map(fn); const differs = new Set(vals).size > 1;
                return (
                  <div key={label} className="grid border-b border-[var(--line)] last:border-0" style={{ gridTemplateColumns: template }}>
                    <div className="p-4 text-sm text-[var(--ink-3)]">{label}</div>
                    {vals.map((v, i) => <div key={i} className={`p-4 text-sm border-l border-[var(--line)] ${differs ? "text-[var(--ink)]" : "text-[var(--ink-2)]"}`}>{v}</div>)}
                    {brokers.length < 3 && <div className="border-l border-[var(--line)]" />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <p className="mt-5 text-xs text-[var(--ink-4)]">Visit links are external broker registration pages (referral). Eligibility depends on your country of residence and the legal entity that serves it.</p>
    </div>
  );
}
